import { approveConvexRequestBody } from '../pages/convex-utils';
import type { ReviewedApproveMarker } from '../pages/convex-utils';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = process.env.REACT_APP_API_URL || '';

export class ApiError extends Error {
  data: any;
  status: number;
  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function getToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  } catch {
    throw new Error('Session expired. Please sign in again.');
  }
}

async function apiCall(path: string, options?: RequestInit): Promise<any> {
  const token = await getToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    const message = errorBody.error || errorBody.detail || `API error: ${response.status}`;
    throw new ApiError(message, response.status, errorBody);
  }
  return response.json();
}

export const api = {
  // Claims
  getClaims: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/claims${query}`);
  },
  getClaim: (id: string) => apiCall(`/claims/${id}`),

  // AI
  summarizeClaim: (id: string) =>
    apiCall(`/claims/${id}/ai/summarize`, { method: 'POST' }),
  analyzeClaim: (id: string) =>
    apiCall(`/claims/${id}/ai/analyze`, { method: 'POST' }),
  draftCorrespondence: (id: string, body: any) =>
    apiCall(`/claims/${id}/ai/draft`, { method: 'POST', body: JSON.stringify(body) }),

  // BDX (Lloyd's format — existing)
  getCoverholders: () => apiCall('/bdx/coverholders'),
  generateBdx: (companyId: string, periodEnd: string, invoiceDate: string) =>
    apiCall('/bdx/generate', {
      method: 'POST',
      body: JSON.stringify({ company_id: companyId, period_end: periodEnd, invoice_date: invoiceDate }),
    }),

  // BDX Coverholder (billing bordereaux — new)
  getBdxCoverholderConfigs: () => apiCall('/bdx-coverholder/configs'),
  getBdxCoverholderConfig: (id: string) => apiCall(`/bdx-coverholder/configs/${id}`),
  saveBdxCoverholderConfig: (config: any) =>
    apiCall('/bdx-coverholder/configs', { method: 'POST', body: JSON.stringify(config) }),
  getBdxQueue: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/bdx-coverholder/queue${query}`);
  },
  getBdxReport: (reportId: string) => apiCall(`/bdx-coverholder/queue/${reportId}`),
  generateBdxCoverholder: (body: { coverholder_id: string; billing_period: string }) =>
    apiCall('/bdx-coverholder/generate', { method: 'POST', body: JSON.stringify(body) }),
  approveBdxReport: (reportId: string, body: { approved_by: string }) =>
    apiCall(`/bdx-coverholder/queue/${reportId}/approve`, { method: 'POST', body: JSON.stringify(body) }),
  getBdxDownload: (reportId: string, type?: string) => {
    const query = type ? `?type=${type}` : '';
    return apiCall(`/bdx-coverholder/queue/${reportId}/download${query}`);
  },

  // Loss Runs
  getLossRunCoverholders: () => apiCall('/lossrun/coverholders'),
  generateLossRun: (companyId: string, cutoffDate: string, statusFilter: string) =>
    apiCall('/lossrun/generate', {
      method: 'POST',
      body: JSON.stringify({ company_id: companyId, cutoff_date: cutoffDate, status_filter: statusFilter }),
    }),

  // Analytics / MI Dashboard
  getMiDashboard: () => apiCall('/mi/dashboard'),

  // Claims Health Monitor
  getClaimsHealth: () => apiCall('/claims-health'),

  // Diary
  getDiary: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/diary${query}`);
  },

  // Handler Workload
  getHandlerWorkload: () => apiCall('/handlers/workload'),

  // Support
  getHandlers: () => apiCall('/handlers'),
  getCompanies: (search?: string) =>
    apiCall(`/companies${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getHealth: () => apiCall('/health'),

  // Change Requests
  getChanges: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/changes${query}`);
  },
  getChange: (id: string) => apiCall(`/changes/${id}`),
  createChange: (body: any) =>
    apiCall('/changes', { method: 'POST', body: JSON.stringify(body) }),
  reviewChange: (id: string, body: { action: string; note?: string }) =>
    apiCall(`/changes/${id}/review`, { method: 'PUT', body: JSON.stringify(body) }),
  retryChange: (id: string) =>
    apiCall(`/changes/${id}/retry`, { method: 'POST' }),
  getChangeStats: () => apiCall('/changes/stats'),
  batchReview: (body: { request_ids: string[]; action: string; note?: string }) =>
    apiCall('/changes/batch-review', { method: 'POST', body: JSON.stringify(body) }),
  getChangePreview: (claimId: string, fields: string[]) =>
    apiCall('/changes/preview', {
      method: 'POST',
      body: JSON.stringify({ claim_id: claimId, fields }),
    }),

  // Precedent Search
  precedentSearch: (body: {
    query: string;
    lob?: string;
    coverage_type?: string;
    limit?: number;
  }) =>
    apiCall('/claims/precedent-search', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Diary Intelligence
  getDiaryManagerStats: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/diary/manager-stats${query}`);
  },
  createDiaryReminder: (body: {
    claim_id: string;
    due_date: string;
    note: string;
  }) =>
    apiCall('/diary/create-reminder', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Time Tracking
  addTimeEntry: (body: {
    claim_id: string;
    hours: number;
    activity_type: string;
    notes?: string;
    claim_reference?: string;
  }) =>
    apiCall('/time', { method: 'POST', body: JSON.stringify(body) }),
  getClaimTime: (claimId: string) => apiCall(`/time/claim/${claimId}`),
  getHandlerTime: (handlerId: string, params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/time/handler/${handlerId}${query}`);
  },
  getTimeSummary: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/time/summary${query}`);
  },

  // Convex Loss Run
  getConvexRequests: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/convex/requests${query}`);
  },
  getConvexRequest: (id: string) => apiCall(`/convex/requests/${id}`),
  updateConvexRequest: (id: string, body: any) =>
    apiCall(`/convex/requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // opts.overrideDuplicateHold: LEGACY (Amos-69 de-gate) — the backend
  // accepts and ignores this key; kept only for call-shape compatibility.
  // Historical: "Send anyway" — bypassed the rule-b
  // duplicate_hold guard for a single record (Amos-20 A7).
  // opts.bulk (R2v4): set by the bulk-approve loop; the backend refuses (409) a
  // sent-before card on this path — only a human per-card approve sends it.
  // opts.reviewedApprove (ruling 3, 2026-10-09): the single-card reviewed-approve marker for a
  // blocking sent-before card. Never sent together with bulk (the backend refuses that pair).
  approveConvexRequest: (id: string, opts?: { overrideDuplicateHold?: boolean; bulk?: boolean; reviewedApprove?: ReviewedApproveMarker | null }) =>
    apiCall(`/convex/requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(approveConvexRequestBody(opts)),
    }),
  // opts.reason: machine-readable archived_reason enum (e.g.
  // 'duplicate_of_prior_send' for the "Archive as duplicate" action).
  // opts.note: additive free text (archived_note), never replaces the enum.
  archiveConvexRequest: (id: string, opts?: { reason?: string; note?: string }) =>
    apiCall(`/convex/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        action: 'archive',
        ...(opts?.reason ? { reason: opts.reason } : {}),
        ...(opts?.note ? { note: opts.note } : {}),
      }),
    }),
  // Amos-20 A7: disposition a rule-b bulk-approve dedup-guard skip so the
  // record exits the Gate Eligible pool instead of re-entering every pass.
  duplicateHoldConvexRequest: (id: string, evidence: {
    prior_sent_at?: string;
    prior_message_id?: string;
    prior_internet_message_id?: string;
    prior_request_id?: string;
    recipient_to?: string[];
  }) =>
    apiCall(`/convex/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'duplicate_hold', evidence }),
    }),
  batchTriage: (body: { request_ids: string[]; action: 'archive' | 'manual_send_archive' | 'delete'; reason?: string }) =>
    apiCall('/convex/requests', { method: 'POST', body: JSON.stringify(body) }),
  // Amos-64: review marker — audit metadata only, never read by approve/send
  // gates. source distinguishes the explicit button ('manual') from
  // post-action auto-stamps ('auto:approve', ...).
  markConvexReviewed: (id: string, source: string) =>
    apiCall(`/convex/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'mark_reviewed', source }),
    }),
  getConvexMasterUploads: () => apiCall('/convex/master-uploads'),
  uploadConvexMaster: async (file: File) => {
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((s, b) => s + String.fromCharCode(b), ''),
    );
    return apiCall('/convex/master-upload', {
      method: 'POST',
      body: JSON.stringify({ filename: file.name, file_data: base64 }),
    });
  },
  getConvexSendMode: async (): Promise<string> => {
    const data = await apiCall('/convex/requests?triage_class=C');
    return data.send_mode || 'off';
  },
  pollConvexMailbox: () => apiCall('/convex/poll-mailbox', { method: 'POST' }),

  // Convex Feedback
  saveConvexFeedback: (body: { feedback: string; reference_number?: string; page_url?: string; request_id?: string }) =>
    apiCall('/convex/feedback', { method: 'POST', body: JSON.stringify(body) }),
  getConvexFeedback: () => apiCall('/convex/feedback'),
  // Amos-64: mark a feedback entry resolved (rides the same POST route).
  resolveConvexFeedback: (feedbackId: string, note?: string) =>
    apiCall('/convex/feedback', {
      method: 'POST',
      body: JSON.stringify({ action: 'resolve', feedback_id: feedbackId, ...(note ? { note } : {}) }),
    }),

  // SLA Reporting (v2)
  getSlaMetrics: () => apiCall('/sla/metrics'),
  getSlaOrgs: () => apiCall('/sla/orgs'),
  createSlaOrg: (config: any) =>
    apiCall('/sla/orgs', { method: 'POST', body: JSON.stringify(config) }),
  getSlaOrg: (orgId: string) => apiCall(`/sla/orgs/${orgId}`),
  updateSlaOrg: (orgId: string, config: any) =>
    apiCall(`/sla/orgs/${orgId}`, { method: 'PUT', body: JSON.stringify(config) }),
  deleteSlaOrg: (orgId: string) =>
    apiCall(`/sla/orgs/${orgId}`, { method: 'DELETE' }),
  generateSlaReport: (body: {
    org_id: string;
    start_date: string;
    end_date: string;
    report_date?: string;
    manual_overrides?: Record<string, number>;
    trigger_type?: string;
  }) =>
    apiCall('/sla/generate', { method: 'POST', body: JSON.stringify(body) }),
  generateAllDue: () =>
    apiCall('/sla/generate-all-due', { method: 'POST' }),

  // SLA Queue
  getSlaQueue: (params?: { org_id?: string; triage?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.org_id) qs.set('org_id', params.org_id);
    if (params?.triage) qs.set('triage', params.triage);
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return apiCall(`/sla/queue${query ? `?${query}` : ''}`);
  },
  getSlaQueueReport: (reportId: string) => apiCall(`/sla/queue/${reportId}`),
  getSlaQueueDownload: (reportId: string) => apiCall(`/sla/queue/${reportId}/download`),
  getSlaQueuePreview: (reportId: string) => apiCall(`/sla/queue/${reportId}/preview`),
  approveSlaReport: (reportId: string, body: { manual_overrides?: Record<string, number> }) =>
    apiCall(`/sla/queue/${reportId}/approve`, { method: 'POST', body: JSON.stringify(body) }),
  sendSlaReport: (reportId: string, body?: { additional_recipients?: any[] }) =>
    apiCall(`/sla/queue/${reportId}/send`, { method: 'POST', body: JSON.stringify(body || {}) }),
  deleteSlaReport: (reportId: string) =>
    apiCall(`/sla/queue/${reportId}`, { method: 'DELETE' }),

  // User Management
  getUsers: () => apiCall('/users'),
  getUser: (userId: string) => apiCall(`/users/${userId}`),
  createUser: (body: { email: string; temporary_password?: string; send_invite?: boolean }) =>
    apiCall('/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (userId: string, body: { email?: string }) =>
    apiCall(`/users/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetUserPassword: (userId: string, body: { new_password: string }) =>
    apiCall(`/users/${userId}/reset-password`, { method: 'POST', body: JSON.stringify(body) }),
  toggleUserStatus: (userId: string, body: { enabled: boolean }) =>
    apiCall(`/users/${userId}/toggle-status`, { method: 'POST', body: JSON.stringify(body) }),

  // Program Configuration
  getPrograms: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/programs${query}`);
  },
  getProgram: (programId: string) => apiCall(`/programs/${programId}`),
  createProgram: (body: any) =>
    apiCall('/programs', { method: 'POST', body: JSON.stringify(body) }),
  updateProgram: (programId: string, body: any) =>
    apiCall(`/programs/${programId}`, { method: 'PUT', body: JSON.stringify(body) }),
  patchProgram: (programId: string, body: any) =>
    apiCall(`/programs/${programId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteProgram: (programId: string) =>
    apiCall(`/programs/${programId}`, { method: 'DELETE' }),

  // Guide Feedback
  saveGuideFeedback: (body: { module: string; reviewer: string; notes: string; session_id: string }) =>
    apiCall('/guide/feedback', { method: 'POST', body: JSON.stringify(body) }),
  getGuideFeedback: (module: string) => apiCall(`/guide/feedback?module=${encodeURIComponent(module)}`),
  getAllGuideFeedback: () => apiCall('/guide/feedback/all'),

  // Admin (Observability)
  getAdminHealth: () => apiCall('/admin/health'),
  getAdminAudit: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiCall(`/admin/audit${query}`);
  },
};

